import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Database,
  Pencil,
  Save,
  X,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Search,
  RefreshCw,
  Wifi,
  WifiOff
} from 'lucide-react'
import { ApiService } from '@/services/api'
import type { Restaurant } from '@/types'

interface RestaurantRow {
  restaurant: Restaurant
  editValue: string
  isEditing: boolean
  isSaving: boolean
  isTesting: boolean
  testResult: 'success' | 'failed' | null
  saveError: string | null
}

export default function DbManagementScreen() {
  const [rows, setRows] = useState<RestaurantRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    loadRestaurants()
  }, [])

  const loadRestaurants = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await ApiService.getAllRestaurants()
      if (response.success) {
        const data = response.data as Restaurant[]
        setRows(
          data.map((r) => ({
            restaurant: r,
            editValue: r.databaseName || '',
            isEditing: false,
            isSaving: false,
            isTesting: false,
            testResult: null,
            saveError: null,
          }))
        )
      } else {
        setError('Failed to load restaurants')
      }
    } catch {
      setError('Failed to load restaurants')
    } finally {
      setIsLoading(false)
    }
  }

  const startEdit = (id: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.restaurant._id === id
          ? { ...row, isEditing: true, editValue: row.restaurant.databaseName || '', saveError: null, testResult: null }
          : row
      )
    )
  }

  const cancelEdit = (id: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.restaurant._id === id
          ? { ...row, isEditing: false, editValue: row.restaurant.databaseName || '', saveError: null }
          : row
      )
    )
  }

  const setEditValue = (id: string, value: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.restaurant._id === id ? { ...row, editValue: value } : row
      )
    )
  }

  const saveDbName = async (id: string) => {
    const row = rows.find((r) => r.restaurant._id === id)
    if (!row) return

    const newName = row.editValue.trim()
    if (!newName) {
      setRows((prev) =>
        prev.map((r) =>
          r.restaurant._id === id ? { ...r, saveError: 'Database name cannot be empty' } : r
        )
      )
      return
    }

    setRows((prev) =>
      prev.map((r) =>
        r.restaurant._id === id ? { ...r, isSaving: true, saveError: null } : r
      )
    )

    try {
      const response = await ApiService.updateRestaurant(id, { databaseName: newName })
      if (response.success) {
        setRows((prev) =>
          prev.map((r) =>
            r.restaurant._id === id
              ? {
                  ...r,
                  restaurant: { ...r.restaurant, databaseName: newName },
                  isEditing: false,
                  isSaving: false,
                  editValue: newName,
                }
              : r
          )
        )
        setSuccessMessage(`Database name updated for "${row.restaurant.name}"`)
        setTimeout(() => setSuccessMessage(null), 3000)
      } else {
        setRows((prev) =>
          prev.map((r) =>
            r.restaurant._id === id
              ? { ...r, isSaving: false, saveError: 'Failed to save' }
              : r
          )
        )
      }
    } catch {
      setRows((prev) =>
        prev.map((r) =>
          r.restaurant._id === id
            ? { ...r, isSaving: false, saveError: 'Failed to save' }
            : r
        )
      )
    }
  }

  const testConnection = async (id: string) => {
    const row = rows.find((r) => r.restaurant._id === id)
    if (!row) return

    setRows((prev) =>
      prev.map((r) =>
        r.restaurant._id === id ? { ...r, isTesting: true, testResult: null } : r
      )
    )

    try {
      const response = await ApiService.testDatabaseConnection(row.restaurant.databaseName)
      setRows((prev) =>
        prev.map((r) =>
          r.restaurant._id === id
            ? { ...r, isTesting: false, testResult: response.success && response.data?.connected ? 'success' : 'failed' }
            : r
        )
      )
    } catch {
      setRows((prev) =>
        prev.map((r) =>
          r.restaurant._id === id ? { ...r, isTesting: false, testResult: 'failed' } : r
        )
      )
    }
  }

  const filtered = rows.filter(
    (row) =>
      row.restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row.restaurant.databaseName || '').toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">DB Management</h2>
          <p className="text-muted-foreground">View and edit database assignments for each restaurant</p>
        </div>
        <Button variant="outline" onClick={loadRestaurants} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {successMessage && (
        <Alert className="border-green-500 text-green-700 bg-green-50 dark:bg-green-950 dark:text-green-400">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Restaurant Databases
              </CardTitle>
              <CardDescription>
                {rows.length} restaurant{rows.length !== 1 ? 's' : ''} registered
              </CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search restaurants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchTerm ? 'No restaurants match your search' : 'No restaurants found'}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Header row */}
              <div className="grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b">
                <div className="col-span-3">Restaurant</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-4">Database Name</div>
                <div className="col-span-3 text-right">Actions</div>
              </div>

              {filtered.map(({ restaurant, editValue, isEditing, isSaving, isTesting, testResult, saveError }) => (
                <div
                  key={restaurant._id}
                  className="grid grid-cols-12 gap-4 items-center px-4 py-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                >
                  {/* Restaurant info */}
                  <div className="col-span-3 min-w-0">
                    <p className="font-medium truncate">{restaurant.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{restaurant.email}</p>
                  </div>

                  {/* Status */}
                  <div className="col-span-2">
                    <Badge
                      variant={restaurant.isActive ? 'default' : 'secondary'}
                      className={
                        restaurant.subscriptionStatus === 'active'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : restaurant.subscriptionStatus === 'expired'
                          ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                      }
                    >
                      {restaurant.subscriptionStatus}
                    </Badge>
                  </div>

                  {/* DB name - view or edit */}
                  <div className="col-span-4">
                    {isEditing ? (
                      <div className="space-y-1">
                        <Input
                          value={editValue}
                          onChange={(e) => setEditValue(restaurant._id, e.target.value)}
                          className="h-8 font-mono text-sm"
                          placeholder="e.g. ordore-restaurant-name"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveDbName(restaurant._id)
                            if (e.key === 'Escape') cancelEdit(restaurant._id)
                          }}
                          autoFocus
                        />
                        {saveError && (
                          <p className="text-xs text-destructive">{saveError}</p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-muted px-2 py-0.5 rounded font-mono truncate max-w-[180px]">
                          {restaurant.databaseName || <span className="text-muted-foreground italic">not set</span>}
                        </code>
                        {testResult === 'success' && (
                          <Wifi className="h-4 w-4 text-green-500 shrink-0" />
                        )}
                        {testResult === 'failed' && (
                          <WifiOff className="h-4 w-4 text-red-500 shrink-0" />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="col-span-3 flex items-center justify-end gap-2">
                    {isEditing ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => cancelEdit(restaurant._id)}
                          disabled={isSaving}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => saveDbName(restaurant._id)}
                          disabled={isSaving}
                        >
                          {isSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                          <span className="ml-1">Save</span>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => testConnection(restaurant._id)}
                          disabled={isTesting || !restaurant.databaseName}
                          title="Test DB connection"
                        >
                          {isTesting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Wifi className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEdit(restaurant._id)}
                        >
                          <Pencil className="h-4 w-4" />
                          <span className="ml-1">Edit</span>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
